import React, { useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import { getProducts } from '../firebase/services';
import SEOHelmet from '../components/SEOHelmet';
import { generateBreadcrumbSchema } from '../utils/seo';
import '../styles/Shop.css';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const productsData = await getProducts();
      if (productsData.length === 0) {
        // Initialize with default products if none exist
        await initializeDefaultProducts();
        const newProductsData = await getProducts();
        setProducts(newProductsData);
        extractCategories(newProductsData);
      } else {
        setProducts(productsData);
        extractCategories(productsData);
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractCategories = (productsData) => {
    const uniqueCategories = ['All', ...new Set(productsData.map(p => p.category).filter(Boolean))];
    setCategories(uniqueCategories);
  };

  const filteredProducts = products
    .filter(p => p.available !== false)
    .filter(p => selectedCategory === 'All' || p.category === selectedCategory)
    .filter(p => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    });

  const initializeDefaultProducts = async () => {
    const { addProduct } = await import('../firebase/services');
    
    const defaultProducts = [
      {
        name: 'Gulab Jamun',
        description: 'Soft and spongy milk-solid balls soaked in sugar syrup',
        longDescription: 'Our signature Gulab Jamun is made with fresh khoya (milk solids) and soaked in aromatic sugar syrup infused with cardamom and rose water. Each piece melts in your mouth, delivering the perfect balance of sweetness and texture.',
        price: 250,
        category: 'Sweets',
        image: 'https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=400',
        ingredients: 'Milk solids, Sugar, Cardamom, Rose water, Ghee',
        available: true,
        variants: [
          {
            name: 'Regular Pack (500g)',
            price: 250,
            description: '8-10 pieces'
          },
          {
            name: 'Family Pack (1kg)',
            price: 450,
            originalPrice: 500,
            description: '18-20 pieces'
          },
          {
            name: 'Party Pack (2kg)',
            price: 850,
            originalPrice: 1000,
            description: '35-40 pieces'
          }
        ],
        nutritionInfo: {
          'Calories': '180 per piece',
          'Protein': '4g',
          'Carbs': '25g',
          'Fat': '8g'
        }
      },
      {
        name: 'Rasgulla',
        description: 'Soft cottage cheese balls in light sugar syrup',
        longDescription: 'Fresh cottage cheese balls cooked to perfection in light sugar syrup. Our Rasgulla is known for its spongy texture and delicate sweetness that has been loved for generations.',
        price: 200,
        category: 'Sweets',
        image: 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=400',
        ingredients: 'Cottage cheese, Sugar, Cardamom',
        variants: [
          {
            name: 'Regular (6 pieces)',
            price: 200,
            description: 'Perfect for small family'
          },
          {
            name: 'Large (12 pieces)',
            price: 380,
            originalPrice: 400,
            description: 'Great for gatherings'
          }
        ]
      },
      {
        name: 'Jalebi',
        description: 'Crispy and juicy deep-fried sweet spirals',
        longDescription: 'Crispy on the outside, juicy on the inside - our Jalebis are made fresh daily with fermented batter and soaked in saffron-infused sugar syrup.',
        price: 180,
        category: 'Sweets',
        image: 'https://images.unsplash.com/photo-1626132647523-66f5bf380027?w=400',
        ingredients: 'Refined flour, Sugar, Saffron, Ghee, Yogurt',
        variants: [
          {
            name: 'Fresh Hot (250g)',
            price: 180,
            description: 'Served warm'
          },
          {
            name: 'Mini Jalebis (500g)',
            price: 320,
            description: 'Bite-sized portions'
          }
        ]
      },
      {
        name: 'Barfi',
        description: 'Traditional milk-based sweet with nuts',
        longDescription: 'Rich and creamy milk-based sweet garnished with premium nuts. Available in multiple flavors to suit every taste preference.',
        price: 300,
        category: 'Sweets',
        image: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400',
        ingredients: 'Milk, Sugar, Nuts, Cardamom, Silver leaf',
        variants: [
          {
            name: 'Plain Barfi (500g)',
            price: 300,
            description: 'Classic milk barfi'
          },
          {
            name: 'Kaju Barfi (500g)',
            price: 450,
            description: 'With cashews'
          },
          {
            name: 'Pista Barfi (500g)',
            price: 480,
            description: 'With pistachios'
          },
          {
            name: 'Mixed Nuts (500g)',
            price: 520,
            description: 'Assorted nuts'
          }
        ]
      },
      {
        name: 'Ladoo',
        description: 'Round sweet balls made with flour and sugar',
        longDescription: 'Traditional round sweets made with premium ingredients. Each variety offers a unique taste and texture, perfect for festivals and celebrations.',
        price: 220,
        category: 'Sweets',
        image: 'https://images.unsplash.com/photo-1606471191009-63d2e1e4a5b6?w=400',
        ingredients: 'Gram flour, Sugar, Ghee, Nuts, Cardamom',
        variants: [
          {
            name: 'Besan Ladoo (500g)',
            price: 220,
            description: 'Made with gram flour'
          },
          {
            name: 'Rava Ladoo (500g)',
            price: 200,
            description: 'Made with semolina'
          },
          {
            name: 'Coconut Ladoo (500g)',
            price: 240,
            description: 'Fresh coconut based'
          }
        ]
      },
      {
        name: 'Kaju Katli',
        description: 'Diamond-shaped cashew fudge',
        longDescription: 'Premium cashew fudge cut into elegant diamond shapes. Made with finest cashews and minimal sugar to highlight the natural nutty flavor.',
        price: 450,
        category: 'Sweets',
        image: 'https://images.unsplash.com/photo-1610192244261-3f33de3f55e4?w=400',
        ingredients: 'Cashews, Sugar, Cardamom, Silver leaf',
        variants: [
          {
            name: 'Regular (250g)',
            price: 450,
            description: '15-18 pieces'
          },
          {
            name: 'Premium (500g)',
            price: 850,
            originalPrice: 900,
            description: '30-35 pieces with silver leaf'
          },
          {
            name: 'Gift Box (1kg)',
            price: 1600,
            originalPrice: 1800,
            description: 'Elegant packaging, 60-70 pieces'
          }
        ],
        nutritionInfo: {
          'Calories': '120 per piece',
          'Protein': '3g',
          'Carbs': '8g',
          'Fat': '9g'
        }
      }
    ];

    // Add each product to Firebase
    for (const product of defaultProducts) {
      await addProduct(product);
    }
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://mdsweets.shop/' },
    { name: 'Shop', url: 'https://mdsweets.shop/shop' }
  ]);

  if (loading) {
    return (
      <div className="shop">
        <div className="loading">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="shop">
      <SEOHelmet 
        title="Shop Traditional Indian Sweets Online - MD Sweets Caterers"
        description="Browse our collection of authentic Indian sweets. Order Gulab Jamun, Rasgulla, Jalebi, Barfi, Ladoo, Kaju Katli and more. Fresh sweets delivered to your doorstep."
        keywords="buy indian sweets online, order sweets online, gulab jamun online, rasgulla online, jalebi online, barfi online, ladoo online, kaju katli online"
        structuredData={breadcrumbSchema}
      />
      <h1>Our Sweets Collection</h1>
      
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search for sweets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        {searchQuery && (
          <button 
            className="clear-search"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>
      
      <div className="category-filter">
        {categories.map(category => (
          <button
            key={category}
            className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="products-count">
        Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
        {selectedCategory !== 'All' && ` in ${selectedCategory}`}
        {searchQuery && ` matching "${searchQuery}"`}
      </div>

      <div className="products-grid">
        {filteredProducts.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="no-products">
          <p>No products found{searchQuery ? ` matching "${searchQuery}"` : ' in this category'}.</p>
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="btn-clear-search">
              Clear Search
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Shop;
